import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import CalendarHeader from './CalendarHeader';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DraftsPanel from './DraftsPanel';
import DayPopup from './DayPopup';
import LessonChip from './LessonChip';
import WorksheetChip from './WorksheetChip';
import { fetchCalendarLessons, fetchDraftLessons, scheduleLesson } from '../../api/lessons';
import { fetchCalendarUnits } from '../../api/classes';
import { fetchSchoolDays, createSchoolDay, deleteSchoolDay } from '../../api/schoolDays';
import { fetchCalendarWorksheets, fetchDraftWorksheets, scheduleWorksheet } from '../../api/worksheets';
import { buildHolidayMap } from '../../constants/holidays';
import styles from './CalendarPage.module.css';

function getDateRange(date, viewMode) {
  const year = date.getFullYear();
  const month = date.getMonth();

  if (viewMode === 'week') {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  // Month view: include overflow days
  const firstDay = new Date(year, month, 1);
  const start = new Date(firstDay);
  start.setDate(start.getDate() - start.getDay());

  const lastDay = new Date(year, month + 1, 0);
  const end = new Date(lastDay);
  if (end.getDay() !== 6) {
    end.setDate(end.getDate() + (6 - end.getDay()));
  }
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

function CalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [lessons, setLessons] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [draftsCount, setDraftsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [dayPopup, setDayPopup] = useState(null);

  // Lifted drafts state
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);

  // Drag overlay state
  const [activeLesson, setActiveLesson] = useState(null);

  // Weekend toggle (persisted)
  const [showWeekends, setShowWeekends] = useState(() => {
    return localStorage.getItem('calendarShowWeekends') === 'true';
  });

  // Holiday state
  const [holidayMap, setHolidayMap] = useState({});
  const [customSchoolDays, setCustomSchoolDays] = useState([]);

  // Units for calendar bands
  const [units, setUnits] = useState([]);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(pointerSensor, keyboardSensor);

  const handleWeekendsToggle = useCallback(() => {
    setShowWeekends(prev => {
      const next = !prev;
      localStorage.setItem('calendarShowWeekends', String(next));
      return next;
    });
  }, []);

  const loadLessons = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { start, end } = getDateRange(currentDate, viewMode);
      const isoStart = start.toISOString();
      const isoEnd = end.toISOString();

      const [lessonResult, schoolDaysResult, unitsResult, worksheetsResult] = await Promise.all([
        fetchCalendarLessons(isoStart, isoEnd),
        fetchSchoolDays(isoStart, isoEnd).catch(() => ({ success: true, data: [] })),
        fetchCalendarUnits(isoStart, isoEnd).catch(() => ({ success: true, data: [] })),
        fetchCalendarWorksheets(isoStart, isoEnd).catch(() => ({ success: true, data: [] })),
      ]);

      if (lessonResult.success) {
        setLessons(lessonResult.data.lessons);
        setDraftsCount(lessonResult.data.drafts);
      }

      if (unitsResult.success) {
        setUnits(unitsResult.data);
      }

      if (worksheetsResult.success) {
        setWorksheets((worksheetsResult.data || []).map(w => ({ ...w, _itemType: 'worksheet' })));
      }

      const custom = schoolDaysResult.success ? schoolDaysResult.data : [];
      setCustomSchoolDays(custom);
      setHolidayMap(buildHolidayMap(start, end, custom));
    } catch (err) {
      setError('Failed to load lessons. Please try again.');
      console.error('Failed to load calendar lessons:', err);
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    loadLessons();
  }, [loadLessons]);

  // Load drafts when panel opens
  useEffect(() => {
    if (!showDrafts) return;
    let cancelled = false;
    (async () => {
      try {
        setDraftsLoading(true);
        const [lessonResult, worksheetResult] = await Promise.all([
          fetchDraftLessons(),
          fetchDraftWorksheets().catch(() => ({ success: true, data: [] })),
        ]);
        if (!cancelled) {
          const lessonDrafts = lessonResult.success
            ? lessonResult.data.map(d => ({ ...d, _itemType: 'lesson' }))
            : [];
          const worksheetDrafts = worksheetResult.success
            ? (worksheetResult.data || []).map(d => ({ ...d, _itemType: 'worksheet' }))
            : [];
          setDrafts([...lessonDrafts, ...worksheetDrafts]);
        }
      } catch (err) {
        console.error('Failed to load drafts:', err);
      } finally {
        if (!cancelled) setDraftsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showDrafts]);

  const handleNavigate = (direction) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      if (viewMode === 'month') {
        next.setMonth(next.getMonth() + direction);
      } else {
        next.setDate(next.getDate() + direction * 7);
      }
      return next;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (date, dayLessons) => {
    const key = date.toISOString().split('T')[0];
    setDayPopup({ date, lessons: dayLessons, holiday: holidayMap[key] || null });
  };

  const handleLessonClick = (lesson) => {
    navigate(`/lessons/${lesson.id}/edit`);
  };

  const handleDraftsToggle = () => {
    setShowDrafts(prev => !prev);
  };

  const handleAddCustomDay = async (date, name) => {
    try {
      const dateStr = date.toISOString().split('T')[0] + 'T12:00:00.000Z';
      const result = await createSchoolDay(dateStr, name);
      if (result.success) {
        const newCustom = [...customSchoolDays, result.data];
        setCustomSchoolDays(newCustom);
        const { start, end } = getDateRange(currentDate, viewMode);
        setHolidayMap(buildHolidayMap(start, end, newCustom));
        // Update popup if open
        const key = date.toISOString().split('T')[0];
        setDayPopup(prev => prev ? { ...prev, holiday: { name, emoji: '\u{1F4CC}', type: 'custom', id: result.data.id } } : null);
      }
    } catch (err) {
      console.error('Failed to create custom day:', err);
    }
  };

  const handleRemoveCustomDay = async (id) => {
    try {
      await deleteSchoolDay(id);
      const newCustom = customSchoolDays.filter(d => d.id !== id);
      setCustomSchoolDays(newCustom);
      const { start, end } = getDateRange(currentDate, viewMode);
      setHolidayMap(buildHolidayMap(start, end, newCustom));
      setDayPopup(prev => prev ? { ...prev, holiday: null } : null);
    } catch (err) {
      console.error('Failed to delete custom day:', err);
    }
  };

  // DnD handlers
  const handleDragStart = (event) => {
    const { lesson } = event.active.data.current;
    setActiveLesson(lesson);
  };

  const handleDragEnd = async (event) => {
    setActiveLesson(null);
    const { active, over } = event;
    if (!over) return;

    const { lesson, source } = active.data.current;
    const target = over.data.current;
    const isWorksheet = lesson._itemType === 'worksheet';
    const scheduleFn = isWorksheet ? scheduleWorksheet : scheduleLesson;
    const itemLabel = isWorksheet ? 'worksheet' : 'lesson';

    // Which state arrays to use for worksheets vs lessons
    const setItems = isWorksheet ? setWorksheets : setLessons;
    const getItems = isWorksheet ? worksheets : lessons;

    // Dropping on drafts panel
    if (target.type === 'drafts') {
      if (source === 'drafts') return; // already a draft

      // Calendar → Drafts: unschedule
      const snapshotItems = [...getItems];
      const snapshotDrafts = [...drafts];
      const snapshotDraftsCount = draftsCount;

      setItems(prev => prev.filter(l => l.id !== lesson.id));
      setDrafts(prev => [...prev, { ...lesson, scheduledDate: null }]);
      if (!isWorksheet) setDraftsCount(prev => prev + 1);

      try {
        await scheduleFn(lesson.id, null);
      } catch (err) {
        setItems(snapshotItems);
        setDrafts(snapshotDrafts);
        if (!isWorksheet) setDraftsCount(snapshotDraftsCount);
        setError(`Failed to unschedule ${itemLabel}. Please try again.`);
      }
      return;
    }

    // Dropping on a day cell
    if (target.type === 'day') {
      const newDate = target.date;
      const newDateStr = newDate.toISOString().split('T')[0];

      // Same-day drop is a no-op
      if (source === 'calendar' && lesson.scheduledDate) {
        const currentDateStr = new Date(lesson.scheduledDate).toISOString().split('T')[0];
        if (currentDateStr === newDateStr) return;
      }

      const scheduledDate = newDateStr + 'T12:00:00.000Z';

      if (source === 'drafts') {
        // Drafts → Calendar
        const snapshotDrafts = [...drafts];
        const snapshotItems = [...getItems];
        const snapshotDraftsCount = draftsCount;

        setDrafts(prev => prev.filter(d => !(d.id === lesson.id && d._itemType === lesson._itemType)));
        setItems(prev => [...prev, { ...lesson, scheduledDate }]);
        if (!isWorksheet) setDraftsCount(prev => prev - 1);

        try {
          await scheduleFn(lesson.id, scheduledDate);
        } catch (err) {
          setDrafts(snapshotDrafts);
          setItems(snapshotItems);
          if (!isWorksheet) setDraftsCount(snapshotDraftsCount);
          setError(`Failed to schedule ${itemLabel}. Please try again.`);
        }
      } else {
        // Calendar → Calendar (different day)
        const snapshotItems = [...getItems];

        setItems(prev =>
          prev.map(l => l.id === lesson.id ? { ...l, scheduledDate } : l)
        );

        try {
          await scheduleFn(lesson.id, scheduledDate);
        } catch (err) {
          setItems(snapshotItems);
          setError(`Failed to reschedule ${itemLabel}. Please try again.`);
        }
      }
    }
  };

  const handleDragCancel = () => {
    setActiveLesson(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className={styles.page}>
        <CalendarHeader
          currentDate={currentDate}
          viewMode={viewMode}
          onViewChange={setViewMode}
          onNavigate={handleNavigate}
          onToday={handleToday}
          draftsCount={draftsCount}
          onDraftsClick={handleDraftsToggle}
          showWeekends={showWeekends}
          onWeekendsToggle={handleWeekendsToggle}
        />
        {error && (
          <div className={styles.error}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            <button className={styles.retryBtn} onClick={loadLessons}>Retry</button>
          </div>
        )}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <span>Loading calendar...</span>
            </div>
          ) : viewMode === 'month' ? (
            <MonthView
              currentDate={currentDate}
              lessons={lessons}
              worksheets={worksheets}
              units={units}
              onDayClick={handleDayClick}
              onLessonClick={handleLessonClick}
              showWeekends={showWeekends}
              holidayMap={holidayMap}
            />
          ) : (
            <WeekView
              currentDate={currentDate}
              lessons={lessons}
              worksheets={worksheets}
              units={units}
              onDayClick={handleDayClick}
              onLessonClick={handleLessonClick}
              showWeekends={showWeekends}
              holidayMap={holidayMap}
            />
          )}
        </div>
        {dayPopup && (
          <DayPopup
            date={dayPopup.date}
            lessons={dayPopup.lessons}
            holiday={dayPopup.holiday}
            onLessonClick={(lesson) => {
              setDayPopup(null);
              handleLessonClick(lesson);
            }}
            onAddLesson={() => {
              const dateStr = dayPopup.date.toISOString().split('T')[0];
              setDayPopup(null);
              navigate(`/lessons/new?date=${dateStr}`);
            }}
            onClose={() => setDayPopup(null)}
            onAddCustomDay={handleAddCustomDay}
            onRemoveCustomDay={handleRemoveCustomDay}
          />
        )}
        {showDrafts && (
          <DraftsPanel
            drafts={drafts}
            loading={draftsLoading}
            onClose={() => setShowDrafts(false)}
            onLessonClick={handleLessonClick}
          />
        )}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeLesson ? (
          activeLesson._itemType === 'worksheet'
            ? <WorksheetChip worksheet={activeLesson} overlay />
            : <LessonChip lesson={activeLesson} overlay />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default CalendarPage;
